// Measures how much the page jumps when Turbo re-renders it under the viewer
// (a live plan refresh, the frames that reload after it, stream renders).
// Off unless localStorage.jank is set: run `localStorage.jank = 1` in the
// console and reload. Pair with `bin/rails "presence:fake[USER,PLAN,,EDIT_EVERY]"`
// to generate edits from another user.
//
// A window opens at the first render event and closes SETTLE_MS after the
// last one, so a page morph and the lazy frames it sets off count as one
// update. Per update it logs:
// - anchorShift: px the thing you were looking at moved on screen (what
//   "jumpy" feels like). Measured on the node itself, or on its nearest
//   [id] ancestor when the render replaced the node; "gone" if both went
// - replaced: the render swapped out the node under your eye (Stimulus
//   reconnects there, and images inside can flash)
// - scroll: change in scrollY
// - cls: summed layout-shift score, with the elements that shifted
// - frames: ids of turbo-frames that rendered inside the window (each is a
//   possible flicker)
// - focusLost / openChanged: focus dropped, expanded/open UI count changed
// `jankReport()` prints every update so far as a table.
const SETTLE_MS = 1000
const OPEN = "[open], .show, .collapse.show, [aria-expanded=true]"

const enabled = (() => {
  try { return !!localStorage.getItem("jank") } catch { return false }
})()

if (enabled) {
  const log = window.jankLog = []
  let current = null

  const describe = node => {
    if (!(node instanceof Element)) return node?.nodeName || "?"
    const id = node.closest("[id]")?.id
    return `${node.tagName.toLowerCase()}${node.classList[0] ? "." + node.classList[0] : ""}${id ? " in #" + id : ""}`
  }

  const open = cause => {
    if (current) return current.causes.add(cause)

    const anchor = document.elementFromPoint(innerWidth / 2, innerHeight / 4)
    const anchorId = anchor?.closest("[id]")?.id
    current = {
      anchorId,
      anchorIdTop: anchorId && document.getElementById(anchorId).getBoundingClientRect().top,
      at: performance.now(),
      causes: new Set([cause]),
      anchor,
      anchorTop: anchor?.getBoundingClientRect().top,
      scrollY,
      focus: document.activeElement,
      openCount: document.querySelectorAll(OPEN).length,
      cls: 0,
      shifted: new Set(),
      frames: []
    }
  }

  const close = () => {
    const c = current
    current = null
    const byId = c.anchorId && document.getElementById(c.anchorId)
    const anchorShift = c.anchor?.isConnected
      ? c.anchor.getBoundingClientRect().top - c.anchorTop
      : byId ? byId.getBoundingClientRect().top - c.anchorIdTop : "gone"
    const entry = {
      at: new Date().toLocaleTimeString(),
      causes: [...c.causes].join(","),
      ms: Math.round(performance.now() - c.at - SETTLE_MS),
      anchorShift: typeof anchorShift === "number" ? Math.round(anchorShift) : anchorShift,
      replaced: !c.anchor?.isConnected,
      scroll: Math.round(scrollY - c.scrollY),
      cls: Number(c.cls.toFixed(3)),
      frames: c.frames.join(" "),
      focusLost: c.focus !== document.body && c.focus !== document.activeElement,
      openChanged: document.querySelectorAll(OPEN).length - c.openCount,
      shifted: [...c.shifted].slice(0, 5).join(" | ")
    }
    log.push(entry)
    const bad = entry.anchorShift !== 0 || entry.scroll !== 0 || entry.cls > 0.05 || entry.focusLost || entry.openChanged
    console[bad ? "warn" : "log"]("[jank]", entry)
  }

  const touch = () => {
    clearTimeout(current.timer)
    current.timer = setTimeout(close, SETTLE_MS)
  }

  for (const [event, cause] of [["turbo:before-render", "page"], ["turbo:before-frame-render", "frame"], ["turbo:before-stream-render", "stream"]]) {
    document.addEventListener(event, e => {
      open(cause === "page" ? `page:${e.detail.renderMethod}` : cause)
      if (cause === "frame") current.frames.push(e.target.id)
      touch()
    })
  }

  new PerformanceObserver(list => {
    if (!current) return
    for (const shift of list.getEntries()) {
      if (shift.hadRecentInput) continue
      current.cls += shift.value
      shift.sources.forEach(source => current.shifted.add(describe(source.node)))
    }
  }).observe({ type: "layout-shift" })

  window.jankReport = () => console.table(log)
}
